/**
 * Perfil do jogador e histórico.
 *
 * Nada aqui decide nada. O nome é validado no banco, o avatar é
 * restringido por `check` na coluna, e o histórico é somado do ledger.
 * Este arquivo pergunta, mostra e manda de volta.
 */
import { baseDaApi } from './presenca.ts';

export interface PerfilPublico {
  readonly userId: string;
  readonly displayName: string;
  readonly avatar: string;
  readonly avatarColor: string;
}

export interface HistoricoJogador {
  readonly partidas: number;
  readonly vitorias: number;
  readonly derrotas: number;
  readonly empates: number;
  readonly apostadoCents: number;
  readonly recebidoCents: number;
  /** Positivo é lucro, negativo é prejuízo. Mostrado como está. */
  readonly saldoDoJogoCents: number;
}

/** Uma partida liquidada, do ponto de vista de quem jogou. */
export interface PartidaDoHistorico {
  readonly matchId: string;
  readonly gameType: string;
  readonly resultado: 'VITORIA' | 'DERROTA' | 'EMPATE';
  readonly variacaoCents: number;
  /** Acumulado corrente até esta partida, com sinal. */
  readonly acumuladoCents: number;
  readonly settledAt: string;
}

export interface PerfilCompleto {
  readonly perfil: PerfilPublico;
  readonly historico: HistoricoJogador;
  readonly trajetoria: readonly PartidaDoHistorico[];
}

export type ResultadoPerfil<T> =
  | { readonly estado: 'ok'; readonly dado: T }
  | { readonly estado: 'erro'; readonly mensagem: string };

const MOTIVOS: Record<string, string> = {
  INVALID_DISPLAY_NAME: 'Nome inválido: use de 2 a 24 caracteres, sem símbolos invisíveis.',
  USER_NOT_FOUND: 'Conta não encontrada.',
  UNAUTHORIZED: 'Sessão expirada. Entre de novo.',
};

async function pedir<T>(caminho: string, init: RequestInit, token: string): Promise<ResultadoPerfil<T>> {
  const base = baseDaApi();
  if (!base) return { estado: 'erro', mensagem: 'API não configurada nesta build.' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}${caminho}`, {
      ...init,
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
        ...(init.headers ?? {}),
      },
    });
  } catch {
    return { estado: 'erro', mensagem: 'O serviço não respondeu.' };
  }

  if (resposta.ok) return { estado: 'ok', dado: (await resposta.json()) as T };

  const corpo = (await resposta.json().catch(() => ({}))) as { error?: unknown };
  const codigo = typeof corpo.error === 'string' ? corpo.error : 'ERRO';
  return { estado: 'erro', mensagem: MOTIVOS[codigo] ?? 'Não foi possível concluir.' };
}

export function carregarPerfil(token: string): Promise<ResultadoPerfil<PerfilCompleto>> {
  return pedir<PerfilCompleto>('/api/profile', { method: 'GET' }, token);
}

export function salvarPerfil(
  token: string,
  displayName: string,
  avatar: string,
  avatarColor: string,
): Promise<ResultadoPerfil<PerfilPublico>> {
  return pedir<PerfilPublico>(
    '/api/profile',
    { method: 'POST', body: JSON.stringify({ displayName, avatar, avatarColor }) },
    token,
  );
}

/**
 * Aproveitamento em porcentagem inteira.
 *
 * Empate não conta como meia vitória: quem empatou recebeu a entrada de
 * volta e não ganhou nada, e inflar o número com meio ponto seria contar
 * uma história melhor do que a que aconteceu.
 */
export function aproveitamento(h: HistoricoJogador): number | null {
  const decididas = h.vitorias + h.derrotas;
  if (decididas === 0) return null;
  return Math.round((h.vitorias / decididas) * 100);
}
